import { RouterModule } from "@angular/router";
import { inject, ModuleWithProviders } from "@angular/core";
import {
	CryptoTransactionCancelService,
	CryptoTransactionConfirmService,
	FiatTransactionCancelService,
	FiatTransactionConfirmService
} from "@main-services/email.actions.service";
import { InitResolver } from "./init.resolver";
import { AuthGuard } from "@main-services/auth-guard.service";
import { AuthCallbackComponent } from "bp-authorization-lib";
import { IndexWalletTransactionComponent } from "@modules/transaction/components/wallet-transaction/index-wallet-transaction/index-wallet-transaction.component";
import { TradableWalletTransactionComponent } from "@modules/transaction/components/wallet-transaction/tradable-wallet-transaction/tradable-wallet-transaction.component";
import { FiatWalletTransactionComponent } from "@modules/transaction/components/wallet-transaction/fiat-wallet-transaction/fiat-wallet-transaction.component";
import { FiatWalletDetailsComponent } from "@modules/main/components/fiat-wallet-details/fiat-wallet-details.component";
import { UserComponent } from "@modules/main/components/user/user.component";
import { SharePriceGuard } from "@main-services/share-price-guard.service";
import { ShareTradeGuard } from "@main-services/share-trade-guard.service";
import { RoutesWithModal } from "@modules/+new/shared/universal-modal/entities/route-with-modal.type";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { ModalHolderComponent } from "@modules/+new/shared/universal-modal/components/holder/modal-holder.component";
import { OpenStatusModalGuard } from "@modules/+new/payments/features/status-transaction/open-status-modal-guard.service";
import { MobileRedirectionsGuard } from "@modules/+new/core/device-redirection-guard/mobile-redirections.guard";
import { VerifiedUserGuard } from "@modules/+new/payments/shared-payments/components/user-verification/infrastructure/verified-user-guard.service";
import {
	API_LEVERAGED_TOKEN_TYPE,
	CASH_PLUS,
	CHANGE_USER_INFO,
	CRYPTO_DEPOSIT_VALIDATION,
	DUST_TO_BEST,
	PSP_REDIRECTION,
	TAXES,
	TIN
} from "@helpers/constants";
import { CashplusFeatureFlagGuard } from "@modules/cashplus/guards/cashplus-feature-flag.guard";
import { ShowcaseService } from "@modules/showcase/showcase.service";
import { TaxesGuardFF } from "@modules/taxes/domain/guards/taxes-guard-ff.service";
import { UserInfoRouteNames } from "@modules/shared/modals/change-personal-info/domain/entities/change-user-info.model";
import { ConfirmEmailGuard } from "@modules/shared/modals/change-personal-info/domain/guards/confirm-email-guard.service";
import { EmptyComponent } from "@modules/main/components/empty/empty.component";
import {
	LimitOrderRouteName,
	OrdersPopupRouteName
} from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import { advancedTradingGuard } from "@modules/advanced-trading/guards/advanced-trading.guard";
import { AutoStakingRoutes } from "@modules/staking/components/auto-staking-confirmation/auto-staking-confirmation.module";
import { Web3MigrationRoutes } from "@modules/web3/w3st-migration.module";
import { web3AutoMigrateGuardFn } from "@modules/web3/guards/web3-base-migrate.guard";
import { EarnRoutes } from "@modules/staking/components/earn-consent-screen/earn-consent-screen.module";
import { tinRedirectResolver } from "@modules/taxes/tin/services/tin-redirect.resolver";
import { HomeComponent } from "@modules/home/components/home.component";
import { securityTransferRedirectResolver } from "@modules/user-reports/security-transfer-redirect.resolver";
import { pricewatchAddModalResolverFn } from "@modules/shared/modals/pricewatch-add-modal/pricewatch-add-modal.resolver";
import { verificationGuard } from "@modules/notifications/guards/verification.guard";

export const routes: RoutesWithModal = [
	{
		path: "",
		redirectTo: "home",
		pathMatch: "full"
	},
	{
		path: "account",
		redirectTo: "/my-account"
	},
	{
		path: "my-account/preferences",
		redirectTo: "/my-account"
	},
	{
		path: "security-transfer/:pid/download",
		component: EmptyComponent,
		resolve: {
			_: securityTransferRedirectResolver
		}
	},
	{
		path: "my-account",
		loadChildren: () => import("app/modules/new_account/new_account.module").then((m) => m.NewAccountModule),
		canActivate: [AuthGuard] // Needed here to load all data. Remove when not necessary
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: CHANGE_USER_INFO,
		loadChildren: () =>
			import("@modules/shared/modals/change-personal-info/change-user-info-routing.module").then(
				(m) => m.ChangePersonalInfoRoutingModule
			),
		canActivate: [AuthGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: DUST_TO_BEST,
		loadChildren: () =>
			import("@modules/portfolio/portfolio-page/portfolio-dust/portfolio-dust.module").then(
				(m) => m.PortfolioDustModule
			),
		canActivate: [AuthGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "v4",
		loadChildren: () => import("@modules/iframeV4/iframeV4-routing.module").then((m) => m.IframeV4RoutingModule),
		canActivate: [AuthGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "send",
		pathMatch: "full",
		redirectTo: "/home?send=true"
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "savings",
		pathMatch: "full",
		redirectTo: "/home?savings=true"
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "trade",
		loadChildren: () => import("@modules/trade/trade-routing.module").then((m) => m.TradeRoutingModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "swap",
		pathMatch: "full",
		redirectTo: "/home(modal:trade/swap)"
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "swap/:asset_pid",
		pathMatch: "full",
		redirectTo: "/home(modal:trade/swap/:asset_pid)"
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "withdraw",
		pathMatch: "full",
		redirectTo: "/home?withdraw=true"
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "withdraw/:fiat_pid",
		pathMatch: "full",
		redirectTo: ({ params }) => `/home?withdraw=true&fiat_pid=${params["fiat_pid"]}`
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "price-alert/:assetId",
		component: ModalHolderComponent,
		resolve: {
			init: InitResolver,
			_: pricewatchAddModalResolverFn
		},
		canActivate: [AuthGuard]
	},
	{
		path: "tin",
		resolve: {
			init: InitResolver,
			_: tinRedirectResolver
		},
		component: HomeComponent,
		pathMatch: "full"
	},
	//deep link for verifications
	{
		path: "crypto-withdrawal/verification",
		resolve: {
			init: InitResolver
		},
		canActivate: [verificationGuard],
		component: HomeComponent,
		pathMatch: "full"
	},
	{
		path: "crypto-deposit/verification",
		resolve: {
			init: InitResolver
		},
		canActivate: [verificationGuard],
		component: HomeComponent,
		pathMatch: "full"
	},
	// path for the iframes that will be shown in  v4 app
	// this maps all of the children of the account eg: iframe/settings; iframe/profile, and any other new routes we need
	{
		path: "iframe",
		loadChildren: () =>
			import("@modules/iframe-provider/iframe-provider.module").then((m) => m.IframeProviderModule),
		canActivate: [AuthGuard]
	},
	{
		path: UserInfoRouteNames.PhoneNumber,
		redirectTo: `/my-account(modal:user-info/${UserInfoRouteNames.PhoneNumber})`
	},
	{
		path: UserInfoRouteNames.EmailAddress,
		redirectTo: `/my-account(modal:user-info/${UserInfoRouteNames.EmailAddress})`
	},
	{
		path: UserInfoRouteNames.Address,
		redirectTo: `/my-account(modal:user-info/${UserInfoRouteNames.Address})`
	},
	{
		path: CRYPTO_DEPOSIT_VALIDATION,
		loadChildren: () =>
			import("@modules/shared/modals/address-book-on-hold-modal/address-book-on-hold-modal.module").then(
				(m) => m.AddressBookOnHoldModalModule
			),
		canActivate: [AuthGuard]
	},
	{
		path: "change-email-confirm",
		component: EmptyComponent,
		canActivate: [ConfirmEmailGuard]
	},
	{
		path: "card",
		redirectTo: "/my-account/card"
	},
	{
		path: "my-best",
		redirectTo: "/my-account/my-best"
	},
	{
		path: "my-vision",
		redirectTo: "/my-account/my-vision"
	},
	{
		path: "contacts",
		redirectTo: "/my-account/contacts"
	},
	{
		path: "security",
		redirectTo: "/my-account/security"
	},
	{
		path: "settings",
		redirectTo: "/my-account/settings"
	},
	{
		path: "trade",
		redirectTo: "home/trade",
		pathMatch: "full"
	},
	// both native apps use "trade/deposit" to deeplink, we need to stay in sync and redirect to the deposit flow in our app
	// NOTE: redirects to auxiliary routes work only with absolute paths, that's why redirectTo starts with "/"
	{
		path: "trade/deposit",
		redirectTo: "/home(modal:fiat-deposit/select-deposit-type)",
		pathMatch: "full"
	},
	// both native apps use "trade/send" as deeplink for SmartWallet, but since we don't have a way to open the send modal, we just redirect the user to the home (to avoid showing a 404 error)
	{
		path: "trade/send",
		redirectTo: "/home",
		pathMatch: "full"
	},
	{
		path: "review-data",
		redirectTo: "home/review-data"
	},
	{
		path: "review-data/edit",
		redirectTo: "home/review-data/edit"
	},
	{
		path: "history",
		redirectTo: "my-account/history"
	},
	{
		path: "savings-plan",
		redirectTo: "my-account/savings"
	},
	{
		path: "affiliate",
		redirectTo: "my-account/affiliate"
	},
	{
		path: "apikey",
		redirectTo: "my-account/apikey"
	},
	{
		path: "terms-privacy",
		redirectTo: "my-account/terms-privacy"
	},
	{
		path: "savings",
		redirectTo: "my-account/savings"
	},
	{
		path: "security",
		redirectTo: "my-account/security"
	},
	{
		path: "tell-a-friend",
		redirectTo: "my-account/tell-a-friend"
	},
	{
		path: "home",
		loadChildren: () => import("app/modules/home/home.module").then((m) => m.HomeModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "trade/:type",
		redirectTo: "home/trade/:type",
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "wallet/:pid",
		component: TradableWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "wallets",
		redirectTo: "portfolio",
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "wallet/:pid/:tid",
		component: TradableWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "index-wallet/:pid",
		component: IndexWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "index-wallet/:pid/:tid",
		component: IndexWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "fiat-wallet-details/:pid",
		component: FiatWalletDetailsComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "fiat-wallet/:pid",
		component: FiatWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		},
		data: { shouldReuse: false }
	},
	{
		path: "fiat-wallet/:pid/transaction/:fpid",
		component: FiatWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: PSP_REDIRECTION,
		component: FiatWalletTransactionComponent,
		canActivate: [MobileRedirectionsGuard, OpenStatusModalGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "deposit/" + PSP_REDIRECTION,
		component: FiatWalletTransactionComponent,
		canActivate: [AuthGuard, OpenStatusModalGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "wallet/:pid/transaction/:fpid",
		component: TradableWalletTransactionComponent,
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "confirm/wallet/:pid/transaction/:tid/confirm/:confirmCode",
		canActivate: [AuthGuard, CryptoTransactionConfirmService],
		component: TradableWalletTransactionComponent,
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "cancel/wallet/:pid/transaction/:tid",
		canActivate: [AuthGuard, CryptoTransactionCancelService],
		component: TradableWalletTransactionComponent,
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "confirm/fiatwallet/:pid/transaction/:tid/confirm/:confirmCode",
		canActivate: [AuthGuard, FiatTransactionConfirmService],
		component: FiatWalletTransactionComponent,
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "cancel/fiatwallet/:pid/transaction/:tid",
		canActivate: [AuthGuard, FiatTransactionCancelService],
		component: FiatWalletTransactionComponent,
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "coins",
		redirectTo: "discover/prices" // This route needs to be modified for UK
	},
	{
		path: "discover",
		redirectTo: "discover/prices" // This route needs to be modified for UK
	},
	{
		path: "highlights",
		redirectTo: "discover/prices" // This route needs to be modified for UK
	},
	// Route for external links, when marketing sends the link in an email to the user
	{
		path: "leveraged-assets",
		redirectTo: "/discover/prices?filter=" + API_LEVERAGED_TOKEN_TYPE
	},
	{
		path: "prices",
		redirectTo: ({ queryParams }) => {
			if (queryParams["symbol"]) {
				return `discover/prices?symbol=${queryParams["symbol"]}`;
			}
			return "discover/prices"; // This route needs to be modified for UK
		}
	},
	{
		path: "movers",
		loadChildren: () => import("@modules/discover/discover.module").then((m) => m.DiscoverModule),
		canActivate: [AuthGuard]
	},
	{
		path: "coin/:symbol",
		redirectTo: "asset/-/:symbol"
	},
	// support for legacy route /price/:symbol
	{
		path: "price/:symbol",
		redirectTo: "asset/-/:symbol"
	},
	{
		path: "discover",
		loadChildren: () => import("@modules/discover/discover.module").then((m) => m.DiscoverModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "asset",
		loadChildren: () =>
			import("app/modules/tradable/tradable-detail/tradable-detail.module").then((m) => m.TradableDetailModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "user/:action",
		pathMatch: "full",
		component: UserComponent,
		canActivate: [AuthGuard]
	},
	{
		path: "user/:action/:key",
		pathMatch: "full",
		component: UserComponent,
		canActivate: [AuthGuard]
	},
	{
		path: "user/:action/:key/:extracode",
		pathMatch: "full",
		component: UserComponent,
		canActivate: [AuthGuard]
	},
	{
		path: "invite/:refId/price/:assetId",
		pathMatch: "full",
		children: [],
		canActivate: [SharePriceGuard]
	},
	{
		path: "invite/:refId/trade/buy/:assetId",
		pathMatch: "full",
		children: [],
		canActivate: [ShareTradeGuard]
	},
	{
		path: "auth-callback",
		pathMatch: "full",
		component: AuthCallbackComponent
	},
	{
		path: "provider",
		pathMatch: "prefix",
		loadChildren: () =>
			import("app/modules/payment-custom-providers/payment-custom-providers.module").then(
				(m) => m.PaymentCustomProvidersModule
			)
	},
	{
		path: "portfolio",
		pathMatch: "full",
		loadChildren: () =>
			import("app/modules/portfolio/portfolio-page/portfolio.module").then((m) => m.PortfolioModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "portfolio/allocation",
		loadChildren: () =>
			import("@modules/portfolio/portfolio-allocation/portfolio-allocation.module").then(
				(m) => m.PortfolioAllocationModule
			),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "provider-zimpler",
		pathMatch: "prefix",
		redirectTo: "provider/zimpler"
	},
	{
		path: "export-history",
		loadChildren: () =>
			import("app/modules/main/components/export-history-modal/export-history-modal.module").then(
				(m) => m.ExportHistoryModalModule
			),
		outlet: "modal",
		canActivate: [AuthGuard]
	},
	// Route for external downloads (e.g. via email) - we have this redirect for compatibility reasons
	{
		path: "download/trades/:filetype",
		pathMatch: "full",
		redirectTo: "my-account/history/export/:filetype"
	},
	// The `modal` verification route is used to open the modal over a current page
	{
		path: "verification",
		outlet: "modal",
		loadChildren: () => import("app/modules/verification/verification.module").then((m) => m.VerificationModule),
		canActivate: [AuthGuard]
	},
	// The original verification routes are preserved here
	{
		path: "verification",
		loadChildren: () => import("app/modules/verification/verification.module").then((m) => m.VerificationModule),
		canActivate: [AuthGuard]
	},
	{
		path: "verification/:type",
		loadChildren: () => import("app/modules/verification/verification.module").then((m) => m.VerificationModule),
		canActivate: [AuthGuard]
	},
	{
		path: "favourites",
		redirectTo: "watchlist"
	},
	{
		path: "watchlist",
		loadChildren: () =>
			import("@modules/watchlist/features/watchlist-page/watchlist-page.module").then(
				(m) => m.WatchlistPageModule
			),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "v1/credit-card-deposit/:transactionId:/:token",
		redirectTo: "provider/paysafe/:transactionId:/:token"
	},
	{
		path: "staking",
		loadChildren: () => import("app/modules/staking/staking.module").then((m) => m.StakingModule)
	},
	{
		path: "custody-account",
		loadChildren: () =>
			import("app/modules/+new/custody_account/custody_account.module").then((m) => m.CustodyAccountModule)
	},
	{
		path: "status",
		outlet: ROUTER_OUTLET_NAME,
		loadChildren: () =>
			import("@modules/+new/payments/features/status-transaction/status-transaction.module").then(
				({ StatusTransactionModule }) => StatusTransactionModule
			)
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "checkout",
		loadChildren: () => import("@modules/checkout/checkout-routing.module").then((m) => m.CheckoutRoutingModule),
		canActivate: [AuthGuard, VerifiedUserGuard],
		resolve: {
			init: InitResolver
		}
	},
	//Notice: this route name is the same as the one for order list, however with the outlet it is a modal route
	{
		outlet: ROUTER_OUTLET_NAME,
		path: OrdersPopupRouteName,
		loadChildren: () =>
			import("@modules/limit-orders/limit-orders.module").then(({ LimitOrdersModule }) => LimitOrdersModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: LimitOrderRouteName.LimitOrderList,
		pathMatch: "full",
		loadChildren: () =>
			import("@modules/limit-orders/limit-orders.module").then(({ LimitOrdersModule }) => LimitOrdersModule),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: EarnRoutes.basePath,
		loadChildren: () =>
			import("@modules/staking/components/earn-consent-screen/earn-consent-screen.module").then(
				({ EarnConsentScreenModule }) => EarnConsentScreenModule
			),
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: AutoStakingRoutes.basePath,
		loadChildren: () =>
			import("@modules/staking/components/auto-staking-confirmation/auto-staking-confirmation.module").then(
				({ AutoStakingConfirmationModule }) => AutoStakingConfirmationModule
			),
		// TODO: make auto staking guard checking for feature flag
		canActivate: [AuthGuard],
		resolve: {
			init: InitResolver
		}
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: Web3MigrationRoutes.basePath,
		loadChildren: () =>
			import("@modules/web3/w3st-migration.module").then(
				({ Web3MigrationModule: Web3MigrationModule }) => Web3MigrationModule
			),
		canActivate: [AuthGuard, web3AutoMigrateGuardFn],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "kyc-refresh",
		redirectTo: "/home(modal:kyc-refresh)"
	},
	{
		path: "nt-migration",
		redirectTo: "/home(modal:web3-migration/auto-migration-intro)"
	},
	{
		path: "proof-of-funds-upload/:case_id/:pof_type",
		redirectTo: "/home(modal:proof-of-funds-upload/:case_id/:pof_type/pof)"
	},
	{
		path: "proof-of-funds-upload/:case_id/:pof_type/:type_id",
		loadChildren: () => import("@modules/pof/pof-routing.module").then((m) => m.PofModalRoutingModule),
		outlet: "modal",
		canActivate: [AuthGuard]
	},
	{
		path: "proof-of-wealth-upload/:case_id/:pof_type",
		redirectTo: "/home(modal:proof-of-wealth-upload/:case_id/:pof_type/pow)"
	},
	{
		path: "proof-of-wealth-upload/:case_id/:pof_type/:type_id",
		loadChildren: () => import("@modules/pof/pof-routing.module").then((m) => m.PofModalRoutingModule),
		outlet: "modal",
		canActivate: [AuthGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: CASH_PLUS,
		loadChildren: () =>
			import("@modules/cashplus/cashplus-routing.module").then((m) => m.CashPlusModalRoutingModule),
		canActivate: [AuthGuard, CashplusFeatureFlagGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: TAXES,
		loadChildren: () => import("@modules/taxes/taxes-routing.module").then((m) => m.TaxesRoutingModule),
		canActivate: [AuthGuard, TaxesGuardFF]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: TIN,
		loadChildren: () => import("@modules/taxes/tin/tin-routing.module").then((m) => m.TinRoutingModule),
		canActivate: [AuthGuard]
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "kyc-refresh",
		loadChildren: () =>
			import("@modules/kyc/components/kyc-refresh/kyc-refresh-routing.module").then(
				(m) => m.KycRefreshRoutingModule
			),
		canActivate: [AuthGuard]
	},
	{
		path: "crypto-taxes",
		redirectTo: "/home(modal:taxes/deeplink)"
	},
	{
		path: "fusion",
		outlet: ROUTER_OUTLET_NAME,
		loadChildren: () =>
			import("@modules/advanced-trading/advanced-trading-routing.module").then(
				(m) => m.AdvancedTradingRoutingModule
			),
		canActivate: [advancedTradingGuard]
	},
	{
		path: "advanced",
		outlet: ROUTER_OUTLET_NAME,
		loadChildren: () =>
			import("@modules/advanced-trading/advanced-trading-routing.module").then(
				(m) => m.AdvancedTradingRoutingModule
			),
		canActivate: [advancedTradingGuard]
	},
	{
		path: "advanced",
		redirectTo: "/home(modal:advanced/terms)"
	},
	{
		path: "fusion",
		redirectTo: "/home(modal:fusion/terms)"
	},
	{
		path: "user-report",
		redirectTo: ({ queryParams }) => {
			if (queryParams["type"]) {
				return `/my-account/reports/list?type=${queryParams["type"]}&source=deeplink`;
			}
			return "/my-account/reports";
		}
	},
	{
		outlet: ROUTER_OUTLET_NAME,
		path: "appropriateness-test",
		loadChildren: () =>
			import("@modules/shared/bp-appropriateness-test/bp-appropriateness-test.module").then(
				(m) => m.BpAppropriatenessTestModule
			)
	},
	{
		path: "notifications/:token",
		pathMatch: "full",
		loadChildren: () =>
			import("@modules/new_account/features/public-notification-center/public-notification-center.module").then(
				(m) => m.PublicNotificationCenterModule
			)
	},
	{
		path: "showcase",
		loadChildren: () => import("@modules/showcase/showcase.module").then((m) => m.ShowcaseModule),
		canActivate: [AuthGuard, () => inject(ShowcaseService).isStaging()],
		resolve: {
			init: InitResolver
		}
	},
	{
		path: "anti-phishing-code",
		redirectTo: "home/anti-phishing-code"
	},
	{
		path: "*",
		redirectTo: "home/notfound",
		pathMatch: "full"
	},
	{
		path: "**",
		redirectTo: "home/notfound",
		pathMatch: "full"
	}
];

export const appRouting: ModuleWithProviders<RouterModule> = RouterModule.forRoot(routes, {
	scrollPositionRestoration: "disabled"
});
